import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurface,
  overlay_backgroundFocused,
  overlay_backgroundNotFocused,
  tag_colorTagNewText,
  text_colorTextPrimary,
  white100,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import { flyout_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import {
  font_4xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_lg,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

const preferredWidth = spacing(145)
const width = `min(${preferredWidth}, 100vw)`
const secondaryContentWidth = `min(${preferredWidth}, calc(100vw - ${width}))`

const colorBgBody = base_colorSurface
const colorBgHeader = base_colorRaisedSurface

const PANEL_TAB_HEIGHT = '70px'

export default css`
  .BaseFlyout-Container {
    /* Use flex so that the Content <div> can grow to fill available space */
    display: flex;
    flex-direction: row;
    height: 100%;

    will-change: transform, opacity;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;

    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);

    &:focus-visible {
      /* It is obvious that the flyout has focus, b/c it is in the foreground */
      outline: none;
    }

    position: absolute;
    top: 0;
    /* Can't use '-width' b/c width isn't a raw value */
    right: calc(-1 * ${width});

    /* Content transition (slide in from right) */
    transition: right ${flyout_transitionSpeed} ease-in-out;
    &.ReactModal__Content--after-open {
      right: 0;
    }
    &.ReactModal__Content--before-close {
      /* Can't use '-width' b/c width isn't a raw value */
      right: calc(-1 * ${width});
    }

    .BaseFlyout-PanelTab {
      cursor: pointer;
      position: absolute;
      top: calc(50% - ${PANEL_TAB_HEIGHT} / 2);
      right: ${width};

      display: inline-flex;
      padding: ${spacing(6)} ${spacing(2)};
      align-items: center;
      gap: ${spacing(1)};
      border-radius: ${cornerRadius} 0 0 ${cornerRadius};

      // Shift on hover to draw the user's gaze
      transition: padding-right 150ms ease-in-out;
      &:hover {
        padding-right: ${spacing(3)};
      }

      // PANEL TAB VARIANTS
      &.new {
        border-top: 1px solid ${base_colorContentBorder};
        border-bottom: 1px solid ${base_colorContentBorder};
        border-left: 1px solid ${base_colorContentBorder};
        background: ${tag_colorTagNewText};

        svg > g > path {
          fill: ${white100};
        }
      }
    }

    .BaseFlyout-Content {
      /* Use flex so that the Content <div> can grow to fill available space */
      display: flex;
      flex-direction: column;
      height: 100%;
      contain: paint;
      width: ${width};

      /* For overscroll */
      background-color: ${colorBgBody};
      &:has(.BaseFlyout-Header) {
        background: linear-gradient(${colorBgHeader}, ${colorBgBody});
      }
      overflow: scroll;

      & > * {
        padding: 0 ${spacing(12)};
      }

      .BaseFlyout-CloseBar {
        padding-top: ${spacing(7)};
        margin-bottom: ${spacing(-2)};

        /* Only match header bg when the header exists */
        background-color: ${colorBgBody};
        &:has(+ .BaseFlyout-Header) {
          background-color: ${colorBgHeader};
        }

        .BaseFlyout-CloseBar-CloseButton {
          margin: ${spacing(-2)};

          &:not(:hover) {
            background-color: transparent;
          }
          &:hover {
            cursor: pointer;
          }
        }
      }

      .BaseFlyout-Header {
        display: flex;
        flex-direction: column;
        gap: ${spacing(4)};

        background-color: ${colorBgHeader};
        padding-top: ${spacing(5)};
        padding-bottom: ${spacing(7)};
        border-bottom: 1px solid ${base_colorContentBorder};

        /* title */
        h2 {
          font-family: ${font_family_weight_medium};
          ${font_4xl}
          color: ${text_colorTextPrimary};
        }

        /* subtitle */
        p {
          font-family: ${font_family_weight_regular};
          ${font_lg}
          color: ${text_colorTextPrimary};
        }
      }

      .BaseFlyout-Body {
        background-color: ${colorBgBody};

        flex-grow: 1;
        padding-top: ${spacing(7)};
        padding-bottom: ${spacing(7)};
      }

      &.secondaryFlyout {
        position: absolute;
        border-right: 1px solid ${base_colorContentBorder};
        width: ${secondaryContentWidth};

        // slide in/out
        transition: right ${flyout_transitionSpeed} ease-in-out;
        right: 0;
        &.showSecondaryContent {
          right: ${width};
        }
      }
    }
  }

  /* Overlay transition (background fade) */
  .BaseFlyout-Overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;

    backface-visibility: hidden;
    contain: paint;

    background-color: ${overlay_backgroundNotFocused};
    opacity: 0;
    transition: opacity ${flyout_transitionSpeed} ease-in-out;
    will-change: opacity;

    &.BaseFlyout-Overlay-open {
      opacity: 1;

      &:has(.focus-background) {
        background-color: ${overlay_backgroundFocused};
      }
    }
    &.BaseFlyout-Overlay-closed {
      opacity: 0;
    }
  }
`
